import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  HomeIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  WrenchScrewdriverIcon,
  CreditCardIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeSolid,
  UserCircleIcon as UserCircleSolid,
  BuildingOfficeIcon as BuildingOfficeSolid,
  WrenchScrewdriverIcon as WrenchScrewdriverSolid,
  CreditCardIcon as CreditCardSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListSolid,
} from "@heroicons/react/24/solid";

interface Props {
  onItemClick?: () => void;
  collapsed?: boolean;
}

const NAV = [
  {
    id: "dashboard",
    name: "Dashboard",
    href: "",
    icon: HomeIcon,
    iconActive: HomeSolid,
  },
  {
    id: "units",
    name: "My Units",
    href: "/units",
    icon: BuildingOfficeIcon,
    iconActive: BuildingOfficeSolid,
  },
  {
    // Explore temporarily hidden per request; kept for future use
    // id: "explore",
    // name: "Explore",
    // href: "/properties",
    // icon: MagnifyingGlassIcon,
    // iconActive: MagnifyingGlassSolid,
  },
  {
    id: "invoices",
    name: "Invoices",
    href: "/invoices",
    icon: CreditCardIcon,
    iconActive: CreditCardSolid,
  },
  {
    // Payments temporarily hidden per request; kept for future use
    // id: "payments",
    // name: "Payments",
    // href: "/payments",
    // icon: CreditCardIcon,
    // iconActive: CreditCardSolid,
  },
  {
    id: "maintenance",
    name: "Maintenance",
    href: "/maintenance",
    icon: WrenchScrewdriverIcon,
    iconActive: WrenchScrewdriverSolid,
  },
  {
    // Documents temporarily hidden per request; kept for future use
    // id: "documents",
    // name: "Documents",
    // href: "/documents",
    // icon: ClipboardDocumentListIcon,
    // iconActive: ClipboardDocumentListSolid,
  },
  {
    id: "profile",
    name: "Profile",
    href: "/profile",
    icon: UserCircleIcon,
    iconActive: UserCircleSolid,
  },
];

const TenantNavigationSidebar: React.FC<Props> = ({
  onItemClick,
  collapsed = false,
}) => {
  const location = useLocation();
  const isActive = (href: string) => {
    if (href === "") {
      return (
        location.pathname === "/tenant" || location.pathname === "/tenant/"
      );
    }
    return (
      location.pathname === `/tenant${href}` ||
      location.pathname.startsWith(`/tenant${href}/`)
    );
  };

  type NavItem = {
    id: string;
    name: string;
    href: string;
    icon: any;
    iconActive: any;
  };
  const items = NAV.filter(
    (i) => (i as any)?.href !== undefined
  ) as unknown as NavItem[];
  return (
    <nav
      className={`flex-1 mt-5 ${
        collapsed ? "px-1" : "px-2"
      } overflow-y-auto overflow-x-visible`}
    >
      <div className="space-y-1 pb-4">
        {items.map((item) => {
          const ActiveIcon = item.iconActive as any;
          const InactiveIcon = item.icon as any;
          const active = isActive(item.href || "");
          return (
            <Link
              key={item.id}
              to={`/tenant${item.href}`}
              className={`group relative flex items-center ${
                collapsed ? "justify-center" : ""
              } px-3 py-2 text-sm font-medium rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                active
                  ? "bg-purple-100 text-purple-900"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
              onClick={onItemClick}
              title={item.name}
              aria-current={active ? "page" : undefined}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 w-1 rounded-r transition-colors ${
                  active
                    ? "bg-primary-600"
                    : "bg-transparent group-hover:bg-gray-200"
                }`}
              />
              <span
                className={`${collapsed ? "" : "mr-3"} flex-shrink-0`}
                aria-hidden="true"
              >
                {active ? (
                  <ActiveIcon className="w-5 h-5" />
                ) : (
                  <InactiveIcon className="w-5 h-5" />
                )}
              </span>
              {!collapsed && <span className="truncate">{item.name}</span>}
              {collapsed && <span className="sr-only">{item.name}</span>}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default TenantNavigationSidebar;
